'use client';

import { BookOpen } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { SetupConnectTutorialContent } from '@/components/setup/experience/SetupConnectTutorialContent';
import {
  getSetupWooConnectTutorialSteps,
  type WooConnectTutorialMode,
} from '@/components/setup/experience/lib/connectTutorialContent';

interface SetupWooOAuthGuidePanelProps {
  /** Which connection method the form is currently showing — drives the step list. */
  mode: WooConnectTutorialMode;
  className?: string;
}

/** Always-visible WooCommerce setup guide shown beside the connect form (text-only, no screenshots). */
export function SetupWooOAuthGuidePanel({ mode, className }: SetupWooOAuthGuidePanelProps) {
  const { t } = useTranslation(['setup', 'integrations']);

  const steps = React.useMemo(() => getSetupWooConnectTutorialSteps(t, mode), [t, mode]);

  return (
    <aside className={className} aria-label={t('setup:experience.connect.wooTutorial.segmentsAriaLabel')}>
      <div className="border-border rounded-xl border bg-[color-mix(in_srgb,var(--muted)_12%,var(--card))]">
        <p className="text-muted-foreground m-0 flex items-center gap-1.5 px-5 pb-3 pt-4 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] sm:px-6">
          <BookOpen className="size-3.5" aria-hidden />
          {t('setup:experience.connect.wooTutorial.kicker')}
        </p>

        <SetupConnectTutorialContent
          className="pb-4"
          layout="stacked"
          steps={steps}
          stepOfLabel={(current, total) =>
            t('setup:experience.connect.shopifyTutorialStepOf', { current, total })
          }
          segmentsAriaLabel={t('setup:experience.connect.wooTutorial.segmentsAriaLabel')}
          goToStepAriaLabel={(step) => t('setup:experience.connect.wooTutorial.goToStep', { step })}
        />
      </div>
    </aside>
  );
}
